import * as Sentry from '@sentry/react'
import React from 'react'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'
import env from '../env.json'

interface CustomErrorProps {
  error?: Error
  message?: string
  name: string
  status: number
}

// Environments whose errors and traces are triaged in Sentry, each with the trace sample rate it
// is sampled at. The named feature environments are deliberately absent. Reporting and sampling
// read this one object rather than a list and a parallel switch, so an environment cannot start
// reporting without being sampled, or be sampled without reporting.
const SENTRY_TRACE_SAMPLE_RATES = {
  QA: 0.5,
  UAT: 0.25, // staging environment
  PreProd: 1.0,
  Production: 0.25,
  Autotest: 1.0,
} as const

function isReportingEnvironment(envName: string): envName is keyof typeof SENTRY_TRACE_SAMPLE_RATES {
  return envName in SENTRY_TRACE_SAMPLE_RATES
}

function getRate(envName: string): number {
  return isReportingEnvironment(envName) ? SENTRY_TRACE_SAMPLE_RATES[envName] : 0
}

/**
 * A dev server serving the app off the developer's own machine. Suppressed because the errors it
 * produces are unfinished local work, not anything a user hit — and because the volume of them
 * would drown the real signal.
 *
 * The env name alone cannot tell us this, which is why the hostname is read directly: the
 * committed default env file is a byte-for-byte copy of a real environment's — CI asserts it —
 * so a dev server carries that environment's name like any deployed box of it. That environment
 * is the one the backend deploys to from its own `develop`, sampled at 1.0, so without this
 * guard every developer's localhost would report every trace under it.
 *
 * Deliberately NOT keyed on the deployment hostname: every deployed environment reports, which
 * is what makes the per-environment sample rates above real configuration rather than dead
 * branches, and what lets an error found on a pre-production environment be triaged before it
 * reaches a user.
 */
const isLocalDevServer = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

function scrubSensitiveParams(url: string | undefined): string | undefined {
  if (!url) return url
  try {
    const parsed = new URL(url)
    if (parsed.searchParams.has('accessToken')) {
      parsed.searchParams.set('accessToken', '[Filtered]')
    }
    return parsed.toString()
  } catch {
    return url
  }
}

export function initSentry() {
  Sentry.init({
    // `enabled` rather than a conditional `Sentry.init`, so every `captureException` in the app
    // stays a safe no-op instead of a guard each call site has to remember. This does more than
    // silence the transport, though: `Client.init` gates `_setupIntegrations()` on the same
    // flag, so when it is false the integrations below are never installed at all — no router
    // instrumentation, no session replay recording, and nothing reaching `beforeSend`.
    enabled: isReportingEnvironment(env.name) && !isLocalDevServer,
    dsn: 'https://a287b20aa7f46284667c809f99fed208@o4508456417427456.ingest.us.sentry.io/4509136326557696',
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: getRate(env.name),
    propagateTraceparent: true,
    tracePropagationTargets: [env.baseURL],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: env.name,
    beforeSend(event) {
      if (event.request?.url) {
        // @ts-expect-error TS2412 -- MIGRATION: not approved, fix when encountered
        event.request.url = scrubSensitiveParams(event.request.url)
      }
      return event
    },
    beforeSendTransaction(event) {
      if (event.request?.url) {
        // @ts-expect-error TS2412 -- MIGRATION: not approved, fix when encountered
        event.request.url = scrubSensitiveParams(event.request.url)
      }
      return event
    },
  })
}
export function sendCustomSentryError(e: CustomErrorProps) {
  Sentry.withScope((scope) => {
    const error = e.error || new Error(e.message || 'An unknown error occurred')
    error.name = e.name
    scope.setContext('response', {
      status: e?.status,
    })
    Sentry.captureException(error)
  })
}
export function sendCustomSentryMessage(message: string) {
  Sentry.captureMessage(message)
}
